import {
  createElement,
  div,
  span,
  button,
  input,
  label,
  img,
} from "../utils/dom";
import { show, hide } from "../utils/showhide";
import { ICON_CLOSE, ICON_SEARCH, ICON_SAD_SMILEY } from "../constants/icons";
import type {
  Category,
  Location,
  MapplicLocalization,
  CategoriesMap,
  LocationsMap,
} from "../types";

// Block: mapplic-sidebar
const CLASSNAME_SIDEBAR = "mapplic-sidebar";
const CLASSNAME_SIDEBAR_FILTER = "mapplic-sidebar__filter";
const CLASSNAME_SIDEBAR_TAGS = "mapplic-sidebar__tags";
const CLASSNAME_SIDEBAR_LIST_CONTAINER = "mapplic-sidebar__list-container";

// Block: mapplic-search
const CLASSNAME_SEARCH = "mapplic-search";
const CLASSNAME_SEARCH_ICON = "mapplic-search__icon";
const CLASSNAME_SEARCH_INPUT = "mapplic-search__input";
const CLASSNAME_SEARCH_CLEAR = "mapplic-search__clear";
const CLASSNAME_SEARCH_LIVE_REGION = "mapplic-search__live-region";

// Block: mapplic-list
const CLASSNAME_LIST = "mapplic-list";
const CLASSNAME_LIST_CATEGORY = "mapplic-list__category";
const CLASSNAME_LIST_LOCATION = "mapplic-list__location";
const CLASSNAME_LIST_COUNT = "mapplic-list__count";

// Block: mapplic-category (nested block in list item)
const CLASSNAME_CATEGORY_BUTTON = "mapplic-category__button";
const CLASSNAME_CATEGORY_TITLE = "mapplic-category__title";

// Block: mapplic-location (nested block in list item)
const CLASSNAME_LOCATION_BUTTON = "mapplic-location__button";
const CLASSNAME_LOCATION_TITLE = "mapplic-location__title";

// Block: mapplic-thumbnail
const CLASSNAME_THUMBNAIL = "mapplic-thumbnail";
const CLASSNAME_THUMBNAIL_PLACEHOLDER = "mapplic-thumbnail--placeholder";

// Block: mapplic-tag
const CLASSNAME_TAG = "mapplic-tag";
const CLASSNAME_TAG_REMOVE = "mapplic-tag__remove";

// Block: mapplic-no-results
const CLASSNAME_NO_RESULTS = "mapplic-no-results";
const CLASSNAME_NO_RESULTS_TITLE = "mapplic-no-results__title";

// Utility
const CLASSNAME_SR_ONLY = "mapplic-sr-only";

export interface SidebarOptions {
  searchfields: (keyof Location | keyof Category)[]; // which fields to search in for the keyword
}

export interface SidebarCallbacks {
  onLocationClick: (id: string, triggerElement: HTMLElement) => void;
  getLocationColor: (location: Location) => string | null;
  getContainerElement: () => HTMLElement | null;
}

export class Sidebar {
  // DOM Elements
  public readonly rootElem: HTMLElement;
  private tags: HTMLElement | null;
  private input: HTMLInputElement | null;
  private list: HTMLElement | null;
  private clearBtn: HTMLButtonElement | null;
  private liveRegion: HTMLElement | null;
  private noResults: HTMLElement | null;

  // State - active tag filters
  private taglist: Record<string, boolean> = {};

  constructor(
    private options: SidebarOptions,
    private l10n: MapplicLocalization,
    private callbacks: SidebarCallbacks,
    private categories: CategoriesMap,
    private locations: LocationsMap,
  ) {
    this.rootElem = this.createSidebar();
    this.tags = this.rootElem.querySelector(`.${CLASSNAME_SIDEBAR_TAGS}`);
    this.input = this.rootElem.querySelector(`.${CLASSNAME_SEARCH_INPUT}`);
    this.clearBtn = this.rootElem.querySelector(`.${CLASSNAME_SEARCH_CLEAR}`);
    this.list = this.rootElem.querySelector(`.${CLASSNAME_LIST}`);
    this.liveRegion = this.rootElem.querySelector(`.${CLASSNAME_SEARCH_LIVE_REGION}`);
    this.noResults = this.rootElem.querySelector(`.${CLASSNAME_NO_RESULTS}`);

    this.bindEvents();
  }

  private createSearchInput(): HTMLElement {
    const inputId = "mapplic-search-input-" + Math.random().toString(36).substring(2, 11);
    const hintId = "mapplic-search-hint-" + Math.random().toString(36).substring(2, 11);

    const searchInputWrapper = div({ className: CLASSNAME_SEARCH });

    // Search icon (decorative, inline SVG for high-contrast mode support)
    const searchIcon = span({
      className: CLASSNAME_SEARCH_ICON,
      attributes: { "aria-hidden": "true" },
      html: ICON_SEARCH,
    });
    searchInputWrapper.appendChild(searchIcon);

    // Search label (screen reader only)
    const searchLabel = label({
      className: CLASSNAME_SR_ONLY,
      text: "Geschäft suchen",
      attributes: { for: inputId },
    });
    searchInputWrapper.appendChild(searchLabel);

    // Search input
    const searchInput = input({
      className: CLASSNAME_SEARCH_INPUT,
      attributes: {
        id: inputId,
        type: "text",
        spellcheck: "false",
        placeholder: this.l10n.search,
        "aria-describedby": hintId,
      },
    });
    searchInputWrapper.appendChild(searchInput);

    // Clear button (always in DOM, disabled when empty)
    const clearButton = button({
      className: CLASSNAME_SEARCH_CLEAR,
      attributes: {
        "aria-label": "Suche leeren",
        type: "button",
        disabled: "",
      },
      html: ICON_CLOSE,
    });
    searchInputWrapper.appendChild(clearButton);

    // Search hint for screen readers
    const searchHint = span({
      className: CLASSNAME_SR_ONLY,
      text: "Ergebnisse werden während der Eingabe angezeigt",
      attributes: { id: hintId },
    });
    searchInputWrapper.appendChild(searchHint);

    // Live region for announcing filter/search state
    const liveRegion = div({
      className: `${CLASSNAME_SEARCH_LIVE_REGION} ${CLASSNAME_SR_ONLY}`,
      attributes: {
        "aria-live": "polite",
        "aria-atomic": "true",
      },
    });
    searchInputWrapper.appendChild(liveRegion);

    return searchInputWrapper;
  }

  private createSidebar(): HTMLElement {
    const sidebar = div({ className: CLASSNAME_SIDEBAR });

    // Filter section
    const filter = div({ className: CLASSNAME_SIDEBAR_FILTER });
    sidebar.appendChild(filter);

    const searchInput = this.createSearchInput();
    filter.appendChild(searchInput);

    // Tag chips container (hidden from a11y tree until populated)
    const tags = div({
      className: CLASSNAME_SIDEBAR_TAGS,
      attributes: {
        "aria-hidden": "true",
      },
    });
    filter.appendChild(tags);

    const listContainer = div({ className: CLASSNAME_SIDEBAR_LIST_CONTAINER });
    sidebar.appendChild(listContainer);

    const list = createElement("ul", {
      className: CLASSNAME_LIST,
      attributes: { role: "list" },
    });
    listContainer.appendChild(list);
    this.createCategories(list);
    this.createLocations(list);

    // categories = this.addCategories();

    // No results message (hidden by default)
    const noResults = div({
      className: CLASSNAME_NO_RESULTS,
      html: ICON_SAD_SMILEY
    });

    const noResultsTitle = span({
      className: CLASSNAME_NO_RESULTS_TITLE,
      text: "Keine Ergebnisse gefunden",
    });
    noResults.appendChild(noResultsTitle);

    hide(noResults);
    listContainer.appendChild(noResults);

    return sidebar;
  }

  private bindEvents(): void {
    // Search on text input (not navigation keys)
    this.input?.addEventListener("input", () => {
      this.search();
    });

    // Keyboard navigation from input
    this.input?.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        // Enter selects first visible result
        const firstVisible = this.getVisibleButtons()[0];
        if (firstVisible) {
          firstVisible.click();
        }
      } else if (e.key === "ArrowDown") {
        // ↓ moves to first visible result
        e.preventDefault();
        const firstVisible = this.getVisibleButtons()[0];
        if (firstVisible) {
          firstVisible.focus();
        }
      }
    });

    // Keyboard navigation within list
    this.list?.addEventListener("keydown", (e) => {
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;

      const target = e.target as HTMLElement;
      if (!target.matches(`.${CLASSNAME_LOCATION_BUTTON}, .${CLASSNAME_CATEGORY_BUTTON}`)) return;

      e.preventDefault();

      const visibleButtons = this.getVisibleButtons();
      const currentIndex = visibleButtons.indexOf(target as HTMLButtonElement);

      if (currentIndex === -1) return;

      if (e.key === "ArrowDown") {
        if (currentIndex < visibleButtons.length - 1) {
          // Move to next
          visibleButtons[currentIndex + 1].focus();
        } else {
          // Last element: wrap to input
          this.input?.focus();
        }
      } else if (e.key === "ArrowUp") {
        if (currentIndex > 0) {
          // Move to previous
          visibleButtons[currentIndex - 1].focus();
        } else {
          // First element: wrap to removeBtn (if active) or input
          const removeBtn = this.tags?.querySelector<HTMLButtonElement>(`.${CLASSNAME_TAG_REMOVE}`);
          if (removeBtn) {
            removeBtn.focus();
          } else {
            this.input?.focus();
          }
        }
      }
    });

    // Clear button click
    this.clearBtn?.addEventListener("click", (e) => {
      e.preventDefault();
      if (this.input) {
        this.input.value = "";
      }
      this.search();
    });
  }

  /**
   * Get all visible buttons in the list (categories and locations)
   */
  private getVisibleButtons(): HTMLButtonElement[] {
    if (!this.list) return [];

    const selector = `
      .${CLASSNAME_LIST_CATEGORY}:not([style*="display: none"]) > .${CLASSNAME_CATEGORY_BUTTON},
      .${CLASSNAME_LIST_LOCATION}:not([style*="display: none"]) > .${CLASSNAME_LOCATION_BUTTON}
    `;
    return Array.from(this.list.querySelectorAll<HTMLButtonElement>(selector));
  }

  /**
   * Add a tag filter for a category
   */
  private addTag(category: Category): void {
    if (!this.tags) return;
    if (this.taglist[category.id]) return;

    // Store the category button for focus return
    const categoryButton = category.listElem?.querySelector<HTMLButtonElement>(
      `.${CLASSNAME_CATEGORY_BUTTON}`,
    );

    // Create tag chip
    const tag = div({
      className: CLASSNAME_TAG,
    });

    if (category.color) {
      tag.style.setProperty('--category-color', category.color);
    }

    // Tag label text
    tag.appendChild(document.createTextNode(category.title));

    // Remove button
    const removeBtn = button({
      className: CLASSNAME_TAG_REMOVE,
      attributes: {
        type: "button",
        "aria-label": `Filter ${category.title} entfernen`,
      },
      html: ICON_CLOSE,
    });

    removeBtn.addEventListener("click", () => {
      this.removeTag(category.id, categoryButton);
    });

    // Keyboard navigation from remove button
    removeBtn.addEventListener("keydown", (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        const firstVisible = this.getVisibleButtons()[0];
        if (firstVisible) {
          firstVisible.focus();
        }
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.input?.focus();
      }
    });

    tag.appendChild(removeBtn);
    this.tags.appendChild(tag);
    this.tags.setAttribute("aria-hidden", "false");

    // Update state
    this.taglist[category.id] = true;

    // Clear search input when adding tag
    if (this.input) {
      this.input.value = "";
    }

    // Update display
    this.search();

    // Focus on remove button (after the category button disappears)
    requestAnimationFrame(() => {
      removeBtn.focus();
    });

    // Announce
    const count = category.numberOfLocations;
    this.announce(
      `Filter ${category.title} aktiv. ${count} ${count === 1 ? "Ergebnis" : "Ergebnisse"}.`,
    );
  }

  /**
   * Remove a tag filter
   */
  private removeTag(
    categoryId: string,
    returnFocusTo: HTMLButtonElement | null | undefined,
  ): void {
    delete this.taglist[categoryId];

    if (this.tags) {
      this.tags.innerHTML = "";
      this.tags.setAttribute("aria-hidden", "true");
    }

    if (this.input) {
      this.input.value = "";
    }
    // Update display
    this.search();

    // Focus back on category button
    if (returnFocusTo) {
      requestAnimationFrame(() => {
        returnFocusTo.focus();
      });
    }

    // Announce
    this.announce("Filter entfernt.");
  }

  /**
   * Announce a message to screen readers via live region
   */
  private announce(message: string): void {
    if (!this.liveRegion) return;
  
    // Clear first to ensure re-announcement of same text
    this.liveRegion.textContent = "";
    requestAnimationFrame(() => {
      this.liveRegion!.textContent = message;
    });
  }

  /**
   * Create placeholder thumbnail (color-only indicator)
   */
  private createPlaceholder(): HTMLElement {
    return span({
      className: `${CLASSNAME_THUMBNAIL} ${CLASSNAME_THUMBNAIL_PLACEHOLDER}`,
    });
  }

  /**
   * Add categories to the sidebar
   */
  private createCategories(list: HTMLUListElement): void {

    Object.values(this.categories).forEach((category) => {
      const item = createElement("li", {
        className: CLASSNAME_LIST_CATEGORY,
        attributes: { "data-category": category.id },
      });

      const categoryButton = button({
        className: CLASSNAME_CATEGORY_BUTTON,
        attributes: { type: "button" },
      });
      item.appendChild(categoryButton);

      // Color indicator
      const thumbnail = this.createPlaceholder();
      thumbnail.setAttribute("aria-hidden", "true");
      categoryButton.appendChild(thumbnail);

      if (category.color) {
        // use css variable instead ??
        thumbnail.style.backgroundColor = category.color;
      }

      // Title
      const title = span({
        className: CLASSNAME_CATEGORY_TITLE,
        text: category.title,
      });

      // Count
      const counter = span({
        className: CLASSNAME_LIST_COUNT,
        text: category.numberOfLocations.toString(),
      });
      category.counterElem = counter;

      title.appendChild(counter);
      categoryButton.appendChild(title);

      // Click handler - add tag filter
      categoryButton.addEventListener("click", () => {
        this.addTag(category);
      });

      category.listElem = item;
      list.appendChild(item);
    });
  }

  private createLocations(list: HTMLUListElement): void {
    Object.values(this.locations).forEach((location) => {
      if (location.action !== 'disabled' && location.hide !== 'true' && location.hide !== true) {
        location.listElem = this.addLocation(location, list);
      }
    });
  }

  /**
   * Add a location to the sidebar list
   */
  public addLocation(location: Location, list: HTMLUListElement): HTMLElement {
    const item = createElement("li", {
      className: CLASSNAME_LIST_LOCATION,
      attributes: {
        "data-location": location.id,
        "data-category": location.category[0] || "",
      },
    });

    const locationBtn = button({
      className: CLASSNAME_LOCATION_BUTTON,
      attributes: {
        type: "button",
        "aria-label": `${location.title} auf dem Lageplan anzeigen`,
      },
    });

    locationBtn.addEventListener("click", () => {
      this.callbacks.onLocationClick(location.id, locationBtn);
    });

    // Color
    const color = this.callbacks.getLocationColor(location);
    if (color) {
      item.style.setProperty('--category-color', color);
    }

    // Hide initially
    hide(item);

    // Title
    const title = span({
      className: CLASSNAME_LOCATION_TITLE,
      text: location.title,
    });
    locationBtn.appendChild(title);

    // Thumbnail with alt text
    if (location.image) {
      const thumb = img({
        className: CLASSNAME_THUMBNAIL,
        attributes: {
          src: location.image,
          alt: "",
        },
      });
      locationBtn.appendChild(thumb);
    }
    // } else {
    //   const placeholder = this.createPlaceholder();
    //   placeholder.setAttribute("aria-hidden", "true");
    //   placeholder.style.setProperty('--category-color', color || "transparent");
    //   placeholder.setAttribute("data-category", location.category ? location.category.toString() : "");
    //   locationBtn.appendChild(placeholder);
    // }

    // About/description
    // if (location.about) {
    //   const about = span({
    //     className: "mapplic-about",
    //     html: location.about,
    //   });
    //   locationBtn.appendChild(about);
    // }

    item.appendChild(locationBtn);
    list.appendChild(item);

    return item;
  }

  /**
   * Search/filter the list
   */
  public search(): void {
    const keyword = this.input?.value || "";

    this.updateClearButton(keyword);
    this.filterCategories(keyword);
    const visibleCount = this.filterLocations(keyword);
    this.showResults(keyword, visibleCount);
  }

  /**
   * Update clear button state
   */
  private updateClearButton(keyword: string): void {
    if (this.clearBtn) {
      this.clearBtn.disabled = !keyword;
    }
  }

  /**
   * Get match score for an item (higher = better match)
   * 3 = title starts with keyword
   * 2 = word in title starts with keyword
   * 1 = title contains keyword or other field matches
   * 0 = no match
   */
  private getMatchScore(item: Category | Location, keyword: string): number {
    const lowerKeyword = keyword.toLowerCase();
    const title = (item.title || "").toLowerCase();

    // Title starts with keyword
    if (title.startsWith(lowerKeyword)) {
      return 3;
    }

    // A word in title starts with keyword
    const words = title.split(/\s+/);
    if (words.some(word => word.startsWith(lowerKeyword))) return 2;

    // Title contains keyword anywhere
    if (title.includes(lowerKeyword)) return 1;

    // Check other searchfields
    for (const field of this.options.searchfields) {
      if (field === "title") continue;
      if (!(field in item)) continue;
      const value = (item as any)[field];
      if (typeof value === "string" && value.toLowerCase().includes(lowerKeyword)) {
        return 1;
      }
    }

    return 0;
  }

  /**
   * Filter categories based on search/tags
   */
  private filterCategories(keyword: string): void {
    const hasSearch = keyword.length > 0;
    const hasTags = Object.keys(this.taglist).length > 0;

    Object.values(this.categories).forEach((category) => {
      if (!category.listElem) return;

      if (hasTags || hasSearch) {
        // Tags or search active: hide all categories
        hide(category.listElem!);
      } else {
        // No search, no tags: show all categories
        show(category.listElem!);
      }
    });
  }

  /**
   * Check if a location belongs to all active tag filters
   */
  private matchesAllTags(location: Location): boolean {
    for (const tagId of Object.keys(this.taglist)) {
      if (!location.category.includes(tagId)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Filter locations based on search/tags
   */
  private filterLocations(keyword: string): number {
    const hasSearch = keyword.length > 0;
    const hasTags = Object.keys(this.taglist).length > 0;

    // Collect visible locations with their scores
    const visibleLocations: Array<{ location: Location; score: number }> = [];

    Object.values(this.locations).forEach((location) => {
      if (!location.listElem) return;

      let shown = false;
      let score = 0;

      if (hasTags || hasSearch) {
        if (hasSearch) {
          score = this.getMatchScore(location, keyword);
          shown = score > 0;
        } else if (hasTags) {
          // No search text but tags active: show all locations in tagged categories
          shown = true;
        }

        // AND-filter: if tags active, location must belong to ALL tagged categories
        if (shown && hasTags) {
          shown = this.matchesAllTags(location);
        }
      }

      if (shown) {
        show(location.listElem!);
        visibleLocations.push({ location, score });
      } else {
        hide(location.listElem!);
      }
    });

    // Sort by relevance when searching, otherwise alphabetically
    this.sortVisibleLocations(visibleLocations, hasSearch);

    return visibleLocations.length;
  }

  /**
   * Sort visible locations and reorder DOM
   */
  private sortVisibleLocations(
    locations: Array<{ location: Location; score: number }>,
    byScore: boolean
  ): void {
    if (locations.length === 0) return;

    locations.sort((a, b) => {
      // If searching, sort by score first (descending)
      if (byScore && b.score !== a.score) {
        return b.score - a.score;
      }
      // Then alphabetically
      const titleA = (a.location.title || "").toUpperCase();
      const titleB = (b.location.title || "").toUpperCase();
      return titleA.localeCompare(titleB);
    });

    // Reorder DOM elements
    locations.forEach(({ location }) => {
      if (location.listElem) {
        this.list?.appendChild(location.listElem!);
      }
    });
  }

  /**
   * Show search results (announcement + no-results UI)
   */
  private showResults(keyword: string, visibleCount: number): void {
    const hasSearch = keyword.length > 0;

    // Announce results to screen readers (only for search, not for tag changes)
    if (hasSearch) {
      const message =
        visibleCount === 0
          ? "Keine Ergebnisse gefunden"
          : visibleCount === 1
            ? "1 Ergebnis gefunden"
            : `${visibleCount} Ergebnisse gefunden`;
      this.announce(message);
    }

    // Show/hide no results message and list
    if (hasSearch && visibleCount === 0) {
      if (this.list) {
        hide(this.list);
      }
      if (this.noResults) {
        show(this.noResults);
      }
    } else {
      if (this.list) {
        show(this.list);
      }
      if (this.noResults) {
        hide(this.noResults);
      }
    }
  }

  /**
   * Sort locations alphabetically
   */
  public sort(): void {
    const items = Array.from(
      this.list?.querySelectorAll<HTMLElement>(`.${CLASSNAME_LIST_LOCATION}`) ?? [],
    );

    items.sort((a, b) => {
      const textA = a.textContent?.toUpperCase() ?? "";
      const textB = b.textContent?.toUpperCase() ?? "";
      return textA.localeCompare(textB);
    });

    items.forEach((item) => {
      this.list?.appendChild(item);
    });
  }
}
